import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AlertTriangle } from "lucide-react";

/**
 * Soft warning banner shown on the reported player's own dashboard once they
 * reach a configurable threshold (default 3 of the 10-report auto-ban cap).
 * Reveals COUNT per league only — never reporter identities or reasons. Gives
 * the user a chance to self-correct before the auto-ban triggers.
 */
const MIN_REPORTS_TO_WARN = 3;

export default function ReportWarningBanner() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/users/me/reports-against");
        if (alive) setData(data);
      } catch (e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!data || !data.leagues) return null;
  const worrying = data.leagues.filter((l) => l.count >= MIN_REPORTS_TO_WARN);
  if (worrying.length === 0) return null;

  const maxLeague = worrying[0]; // already sorted desc by count
  const close = maxLeague.count >= Math.max(1, maxLeague.threshold - 2);

  return (
    <div
      data-testid="report-warning-banner"
      className={`bg-[#FFFFFF] dark:bg-[#1A1A1E] border-l-4 ${close ? "border-l-[#E11D48] border-[#E11D48]/60" : "border-l-[#EA580C] border-[#EA580C]/40"} border p-5`}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${close ? "text-[#E11D48]" : "text-[#EA580C]"}`} />
        <div className="flex-1">
          <div className={`font-heading text-base uppercase tracking-widest ${close ? "text-[#E11D48]" : "text-[#EA580C]"}`}>
            {close ? "You are close to being auto-removed" : "Heads up — you have reports against you"}
          </div>
          <div className="font-body text-sm text-[#52525B] dark:text-[#A1A1AA] mt-1 leading-relaxed">
            At <span className="text-[#09090B] dark:text-[#F4F4F5] font-mono">{maxLeague.threshold}</span> unique reports in one league, players are auto-removed and all remaining matches are forfeited. Be respectful, show up for matches, and submit your scores on time.
          </div>
          <ul className="mt-3 space-y-1" data-testid="report-warning-list">
            {worrying.map((l) => (
              <li key={l.league_id} className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#71717A] flex items-center justify-between max-w-md">
                <span>{l.league_name}</span>
                <span className={`${close ? "text-[#E11D48]" : "text-[#EA580C]"}`} data-testid={`report-warning-count-${l.league_id}`}>
                  {l.count} / {l.threshold}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
